import React, { useState, useCallback, useEffect, useReducer } from "react";

import Drawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { Collapse, Fade, LinearProgress } from "@mui/material";
import { isEmpty } from "lodash";
import { ChevronRightOutlined } from "@mui/icons-material";
import Actions from "./Actions";
import SwitchRow from "./SwitchRow";

//TODO refactor to use reducer
/*  
    loading (+)
    data (+)

    startFetching()
    show(data, loading)
*/

const reducer = (state, action) => {
    if (action.type === "SHOW") {
        return {
            ...state,
            data: action.data || {},
            open: true,
            loading: action.loading || false,
        };
    }
    if (action.type === "HIDE") {
        return {
            ...state,
            //data: {},
            open: false,
            //loading: false,
        };
    }

    return state;
};

export const useSideSheet = () => {
    const [state, dispatch] = useReducer(reducer, {
        open: false,
        loading: false,
        data: {},
    });

    const show = useCallback(
        (data, loading) =>
            dispatch({
                type: "SHOW",
                data,
                loading,
            }),
        [dispatch]
    );

    const hide = useCallback(() => dispatch({ type: "HIDE" }), [dispatch]);

    return {
        ...state,
        show,
        hide,
    };
};

export const SideSheetSections = ({ children }) => {
    return (
        <Box
            sx={(theme) => ({
                mt: 3,
                "& .SideSheetSection": {
                    borderBottom: `1px solid ${theme.palette.outlineVariant.main}`,
                },
                "& .SideSheetSection:first-child": {
                    borderTop: `1px solid ${theme.palette.outlineVariant.main}`,
                    borderBottom: `1px solid ${theme.palette.outlineVariant.main}`,
                },
            })}>
            {children}
        </Box>
    );
};

export const useSideSheetSection = (deps, single = true) => {
    const [state, setState] = useState({});
    useEffect(
        () => {
            setState({});
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        Array.isArray(deps) ? deps : []
    );

    return (code) => ({
        open: state[code] || false,
        onOpenChange: () =>
            setState(single ? { [code]: !state[code] } : { ...state, [code]: !state[code] }),
    });
};

export const SideSheetSection = ({
    title,
    renderDescription,
    disabled,
    children,
    actions,
    open,
    onOpenChange,
}) => {
    let description = renderDescription ? renderDescription() : null;
    return (
        <Box sx={{ p: 2 }} className="SideSheetSection">
            <Box sx={{ display: "flex", alignItems: "start", gap: 1 }}>
                {!!onOpenChange && (
                    <Box sx={{ flex: "0 0 auto" }}>
                        <IconButton
                            disabled={disabled}
                            onClick={onOpenChange}
                            sx={(theme) => ({
                                transform: open ? "rotate(90deg)" : "rotate(0deg)",
                                transition: theme.transitions.create("transform", {
                                    duration: theme.transitions.duration.shortest,
                                }),
                            })}>
                            <ChevronRightOutlined />
                        </IconButton>
                    </Box>
                )}
                <Box sx={{ flex: "1 1 auto" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" color={disabled ? "disabled" : "text.primary"}>
                                {title}
                            </Typography>
                            {!isEmpty(description) ? (
                                <Fade in={!open}>
                                    <Typography
                                        variant="caption"
                                        color={disabled ? "text.disabled" : "secondary"}
                                        component="div">
                                        {description}
                                    </Typography>
                                </Fade>
                            ) : null}
                        </Box>
                        {!isEmpty(actions) && <Actions actions={actions} disabled={disabled} />}
                    </Box>
                    {!isEmpty(children) && <Collapse in={!!open}>{children}</Collapse>}
                </Box>
            </Box>
        </Box>
    );
};

export const SideSheetContent = ({ children, noPadding }) => {
    if (noPadding) {
        return <Box sx={{ flex: "1 1 auto" }}>{children}</Box>;
    }
    return (
        <Box sx={{ flex: "1 1 auto" }}>
            <SideSheetContentPadding>{children}</SideSheetContentPadding>
        </Box>
    );
};

export const SideSheetContentPadding = ({ children }) => {
    return <Box sx={{ p: 2 }}>{children}</Box>;
};

export const SideSheetButtons = ({ children }) => {
    return (
        <Box
            sx={{
                flex: "0 0 auto",
                display: "flex",
                gap: 1,
                alignItems: "center",
                justifyContent: "flex-end",
                padding: "24px 16px",
            }}>
            {children}
        </Box>
    );
};

export const SideSheetForm = ({ onSubmit, children }) => {
    return (
        <Box
            sx={{
                flex: "1 1 auto",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                minHeight: 0,
                "& > form ": {
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    minHeight: 0,
                    flex: "1 1 auto",
                },
            }}>
            <form onSubmit={onSubmit} noValidate>
                {children}
            </form>
        </Box>
    );
};

export const SideSheetOptionalSection = ({
    title,
    description,
    name,
    value = false,
    onChange,
    disabled,
    children,
    borderTop,
}) => {
    return (
        <Box
            sx={(theme) => ({
                px: 2,
                borderTop: borderTop ? `1px solid ${theme.palette.outlineVariant.main}` : undefined,
                borderBottom: `1px solid ${theme.palette.outlineVariant.main}`,
            })}>
            <SwitchRow
                title={title}
                description={description}
                name={name}
                value={value || false}
                onChange={onChange}
                disabled={disabled}
                align="right"
            />
            {!isEmpty(children) && (
                <Collapse in={value}>
                    <Box my={2}>{children}</Box>
                </Collapse>
            )}
        </Box>
    );
};

const SideSheet = ({ open, hide, children, title, loading, actions }) => {
    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={hide}
            sx={{
                zIndex: 1201,
                opacity: 0.975,
                "& > .MuiPaper-root": {
                    width: 520,
                    maxWidth: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderTopLeftRadius: 16,
                    borderBottomLeftRadius: 16,
                },
            }}>
            <Box
                sx={{
                    flex: "0 0 auto",
                    padding: "16px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                }}>
                <Typography
                    color="inherit"
                    sx={{ fontWeight: 500, letterSpacing: 0.5, fontSize: 24, flex: "1 1 auto" }}>
                    {title}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                    {!isEmpty(actions) && <Actions actions={actions} disabled={loading} />}
                    <IconButton
                        onClick={() => {
                            !!hide && hide();
                        }}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </Box>
            <Box sx={{ flex: "0 0 auto", display: loading ? "block" : "hidden" }}>
                <LinearProgress />
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", flex: "1 1 auto" }}>
                {children}
            </Box>
        </Drawer>
    );
};

export default SideSheet;
